import { useCallback, useMemo, useRef, useState } from 'react';
import moment from 'moment-timezone';
import { Button } from '@/components/ui/button';
import { insuredColumn, ListTable, type ListColumn, type ListTableHandle } from '@/components/ListTable';
import { ListSearchControls } from '@/components/ListSearchControls/ListSearchControls';
import { ProvideSearchAPIContext, useSearchApiContext } from '@/components/ListSearchControls/contexts';
import type { SearchOptions } from '@/components/ListSearchControls/types';
import { quoteToneClass, StatusBadge, type QuoteTone } from '@/components/statusBadges';
import { useApi, useAuth, useNavigate } from '@/lib/legacy';
import { QuoteStatuses } from '../../constants';
import { applicationSearchOptions } from '../../constants/searchData/searchOptions';
import * as hooks from '../../hooks';
import { normalizeCamelCase } from '../../ui/utils';
import { apiHelpers, formatMoney } from '../../utils';
import { CsvUploadModal as CsvUploadModalJs } from '../SubmitApplicationPage/CsvUploadModal';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: hooks/useAddProductOptionsToSearchOptions.js
const useAddProductOptionsToSearchOptions = (options: unknown): SearchOptions => (hooks as any).useAddProductOptionsToSearchOptions(options);
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- boundary: SubmitApplicationPage/CsvUploadModal.jsx (it stays legacy, D18)
const CsvUploadModal = CsvUploadModalJs as any as (props: {
  open: boolean;
  onClose: () => void;
  onSuccess: (payload: unknown) => void;
}) => JSX.Element;

type Search = { filters?: { field: string; value?: unknown }[] } & Record<string, unknown>;

export type Application = {
  id: string;
  insured?: string;
  product?: { name?: string; productType?: string } | null;
  appliedAt?: string | number | null;
  region?: string;
  postcode?: string;
  locations?: { coveredValue?: number }[];
  quotes?: { status?: string }[] | null;
};

type Chip = { text: string; tone: QuoteTone };

/* The application's status chip, as the legacy list's useFormatApplicationData built its chips: the
   table showed the first chip's colour with the last chip's text. */
export const applicationStatusChip = ({ quotes: quoteList }: Application): Chip => {
  // The API can send null for an application with no quotes.
  const quotes = quoteList ?? [];
  const count = (status: string) => quotes.filter((quote) => quote.status === status).length;
  const released = count(QuoteStatuses.released);
  const pending = count(QuoteStatuses.pending);
  if (!quotes.length) return { text: 'In Review', tone: 'pending' };
  if (count(QuoteStatuses.bound)) return { text: 'Bound', tone: 'bound' };
  if (count(QuoteStatuses.prebound)) return { text: 'Prebound', tone: 'prebound' };
  if (quotes.length > 1) {
    const last = pending ? `${pending} Pending` : released ? `${released} Released` : 'Quoting';
    return { text: last, tone: 'pending' };
  }
  if (released) return { text: 'Quote Ready', tone: 'released' };
  if (pending) return { text: 'Quote Pending', tone: 'pending' };
  if (count(QuoteStatuses.rejected)) return { text: 'Quote Rejected', tone: 'rejected' };
  if (count(QuoteStatuses.expired)) return { text: 'Quote Expired', tone: 'expired' };
  return { text: 'In Review', tone: 'pending' };
};

const totalValue = ({ locations }: Application) =>
  Array.isArray(locations) ? locations.reduce((total, location) => total + (location.coveredValue ? location.coveredValue : 0), 0) : 0;
const applicationPath = (application: Application) => `/applications/${application.id}`;

// Today's table-mode columns (tableColumnsConfig), fixed at each switchable column's default. The
// widths (D36) add up to 796px, which fits the list at 1280px beside a classic 17px scrollbar, the
// insured taking the widest share.
export const applicationColumns: ListColumn<Application>[] = [
  insuredColumn<Application>({
    header: 'INSURED',
    insured: (application) => application.insured,
    text: (application) => application.insured || 'N/A',
    path: applicationPath,
    size: 210,
  }),
  { id: 'product', header: 'PRODUCT', size: 155, accessorFn: ({ product }) => apiHelpers.resolveProduct(product).labels.name },
  {
    id: 'region',
    header: 'REGION',
    size: 100,
    accessorFn: ({ region, postcode }) => `${region || 'N/A'}${postcode && region ? ', ' : ''}${postcode || ''}`,
  },
  {
    id: 'appliedAt',
    header: 'APPLIED',
    size: 100,
    accessorFn: ({ appliedAt }) => (appliedAt ? moment(appliedAt).format('MM-DD-YYYY') : 'N/A'),
  },
  { id: 'totalValue', header: 'TOTAL VALUE', size: 111, accessorFn: (application) => `$${formatMoney(totalValue(application))}` },
  {
    id: 'status',
    header: 'STATUS',
    size: 120,
    accessorFn: (application) => applicationStatusChip(application).text,
    cell: ({ row }) => {
      const chip = applicationStatusChip(row.original);
      return <StatusBadge className={quoteToneClass(chip.tone)}>{normalizeCamelCase(chip.text)}</StatusBadge>;
    },
  },
];

/**
 * The Applications list: the shared search toolbar over one table of applications, as on Policies
 * (D35). The Unquoted switch is taken out of the search and sent as getApplications' second
 * argument, as before.
 */
export const ApplicationsListView = ({ searchPath }: { searchPath?: string }) => {
  const listRef = useRef<ListTableHandle>(null);
  const { getApplications } = useApi();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isInternal = !!user?.isInternal;
  const [csvModalOpen, setCsvModalOpen] = useState(false);

  const handleGetApplications = useCallback(
    async (search: Search) => {
      let unquoted = false;
      if (search && Array.isArray(search.filters)) {
        const newSearch = { ...search, filters: [...search.filters] };
        const quotedIndex = newSearch.filters.findIndex((f) => f.field === 'unquoted');
        if (quotedIndex !== -1) {
          if (newSearch.filters[quotedIndex]?.value === true) {
            unquoted = true;
          }
          newSearch.filters.splice(quotedIndex, 1);
        }
        return await getApplications(newSearch, unquoted);
      }
      return await getApplications(search, unquoted);
    },
    [getApplications]
  );

  const headingRight = useMemo(
    () => (
      <div className="flex flex-row gap-2">
        {isInternal ? <Button onClick={() => setCsvModalOpen(true)}>Create from CSV</Button> : null}
        <Button onClick={() => navigate.to('/submitapplication')}>+ New Application</Button>
      </div>
    ),
    [isInternal, navigate]
  );

  const searchOptions = useAddProductOptionsToSearchOptions(applicationSearchOptions);

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-white px-4 pt-16 pb-6 md:px-[120px]">
      <ProvideSearchAPIContext
        {...(searchPath !== undefined ? { searchPath } : {})}
        searchOptions={searchOptions}
        getData={handleGetApplications as (...args: unknown[]) => Promise<unknown>}
        appSettingsKey="ApplicationsListView"
        cacheKey="applicationlist"
        compareWithout="url"
        keepRowsOnFailedPage
        listRef={listRef}
      >
        <div className="pb-4">
          <ListSearchControls
            heading="Applications"
            searchPlaceholder="Search for applications"
            headingRight={headingRight}
            showDisplayTypeToggle={false}
          />
        </div>
        <Applications tableRef={listRef} />
      </ProvideSearchAPIContext>
      <CsvUploadModal
        open={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        onSuccess={(parsedPayload) => {
          setCsvModalOpen(false);
          if (parsedPayload) {
            navigate.to({ pathname: '/submitapplication', state: { csvSeed: parsedPayload } });
          }
        }}
      />
    </div>
  );
};

const Applications = ({ tableRef }: { tableRef: React.RefObject<ListTableHandle> }) => {
  const { data, loading, loadingFromCache, requestingMore, hasMore, failed, requestMoreData } = useSearchApiContext();
  return (
    <ListTable<Application>
      ref={tableRef}
      rows={data as Application[]}
      columns={applicationColumns}
      rowPath={applicationPath}
      rowId={(application) => String(application.id)}
      label="Applications"
      emptyMessage="No applications to show"
      loading={loading || requestingMore}
      fetching={loading || !!loadingFromCache || requestingMore}
      failed={failed}
      hasMore={hasMore}
      onLoadMore={requestMoreData}
    />
  );
};

export default ApplicationsListView;
